import { Home, FileText, FolderOpen, Mail, Users, Webhook, Gift } from "lucide-react";
import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
const logoImage = "/img/logo-full.webp";

interface AppSidebarProps {
  userType: "submitter" | "funder" | "admin"; // Keep funder for type compatibility even though unused
  userName: string;
  userEmail: string;
  onLogout: () => void;
}

export function AppSidebar({ userType, userName, userEmail, onLogout }: AppSidebarProps) {
  const [location] = useLocation();

  const submitterItems = [
    { title: "Dashboard", url: "/dashboard/submitter", icon: Home },
    { title: "My Deals", url: "/dashboard/submitter/deals", icon: FileText },
  ];

  const adminItems = [
    { title: "Overview", url: "/admin", icon: Home },
    { title: "Users", url: "/admin/users", icon: Users },
    { title: "Deals", url: "/admin/deals", icon: FolderOpen },
    { title: "Promos", url: "/admin/promos", icon: Gift },
    { title: "Messages", url: "/admin/contact-messages", icon: Mail },
    { title: "Webhooks", url: "/admin/webhooks", icon: Webhook },
  ];

  const items = userType === "admin" ? adminItems : submitterItems;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Sidebar data-testid="app-sidebar">
      <SidebarHeader className="p-6 border-b border-sidebar-border">
        <div className="flex items-center gap-2">
          <Link href="/">
            <img 
              src={logoImage} 
              alt="LandPartner" 
              className="h-12 w-auto cursor-pointer"
              data-testid="sidebar-logo"
            />
          </Link>
          <div>
            <p className="text-xs text-muted-foreground capitalize">{userType}</p>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => {
                const isActive = location === item.url;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive} data-testid={`nav-${item.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Link href={item.url}>
                        <item.icon className="w-4 h-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-4 border-t border-sidebar-border">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-xs">{getInitials(userName)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{userName}</p>
            <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onLogout} className="w-full" data-testid="button-logout">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
